import React from 'react';
import { array, func, string } from 'prop-types';
import { propTypes } from '../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MIN_LENGTH,
  LISTING_TITLE_MAX_LENGTH,
} from '../../util/listingPackages';
import { composeValidators, maxLength, required, requiredRichText } from '../../util/validators';
import { FieldTextEditor, FieldTextInput } from '../../components';
import FieldHeader from './FieldHeader';
import ServiceImagesField from './ServiceImagesField';

import css from './EditServiceListingForm.css';

/**
 * Basic information (Figma 258:774): title, images and "About this service".
 * All of them are required for publishing; a draft may leave them empty, so
 * the form treats requiredMessage errors as "not done yet". The same goes for
 * a description under DESCRIPTION_MIN_LENGTH characters.
 */
const BasicInfoSectionComponent = props => {
  const {
    intl,
    requiredMessage,
    images,
    imageUploadError,
    onImageUpload,
    onRemoveImage,
    onManageDisableScrolling,
  } = props;
  const tooLongMessage = max =>
    intl.formatMessage({ id: 'EditServiceListingForm.tooLong' }, { max });
  const tooShortMessage = min =>
    intl.formatMessage({ id: 'EditServiceListingForm.tooShort' }, { min });

  return (
    <section className={css.section}>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="EditServiceListingForm.basicInfoTitle" />
        </h2>
        <p className={css.sectionDescription}>
          <FormattedMessage id="EditServiceListingForm.basicInfoDescription" />
        </p>
      </div>

      <div className={css.field}>
        <FieldHeader
          id="title"
          label={<FormattedMessage id="EditServiceListingForm.titleLabel" />}
          description={<FormattedMessage id="EditServiceListingForm.titleDescription" />}
        />
        <FieldTextInput
          id="title"
          name="title"
          type="text"
          maxLength={LISTING_TITLE_MAX_LENGTH}
          validate={composeValidators(
            required(requiredMessage),
            maxLength(tooLongMessage(LISTING_TITLE_MAX_LENGTH), LISTING_TITLE_MAX_LENGTH)
          )}
        />
      </div>

      <ServiceImagesField
        images={images}
        uploadError={imageUploadError}
        onImageUpload={onImageUpload}
        onRemoveImage={onRemoveImage}
        onManageDisableScrolling={onManageDisableScrolling}
      />

      <div className={css.field}>
        <FieldHeader
          id="description"
          label={<FormattedMessage id="EditServiceListingForm.descriptionLabel" />}
          description={<FormattedMessage id="EditServiceListingForm.descriptionDescription" />}
        />
        <FieldTextEditor
          id="description"
          name="description"
          type="textarea"
          quillClass={css.descriptionEditor}
          isCompact
          withHeading
          maxLength={DESCRIPTION_MAX_LENGTH}
          maxLengthMessage={tooLongMessage(DESCRIPTION_MAX_LENGTH)}
          minLength={DESCRIPTION_MIN_LENGTH}
          minLengthMessage={tooShortMessage(DESCRIPTION_MIN_LENGTH)}
          validate={requiredRichText(requiredMessage)}
        />
      </div>
    </section>
  );
};

BasicInfoSectionComponent.defaultProps = {
  imageUploadError: null,
};

BasicInfoSectionComponent.propTypes = {
  intl: intlShape.isRequired,
  requiredMessage: string.isRequired,
  images: array.isRequired,
  imageUploadError: propTypes.error,
  onImageUpload: func.isRequired,
  onRemoveImage: func.isRequired,
  onManageDisableScrolling: func.isRequired,
};

const BasicInfoSection = injectIntl(BasicInfoSectionComponent);

export default BasicInfoSection;
